// Business Map Settings API Service

import { supabase } from '../lib/supabase';

export interface BusinessMapSettings {
  id?: string;
  business_id: string;
  home_lat?: number;
  home_lng?: number;
  home_address?: string;
  default_zoom?: number;
  map_type?: string;
  created_by?: string;
  updated_by?: string;
  created_at?: string;
  updated_at?: string;
}

export interface BusinessHomePin {
  lat: number;
  lng: number;
  address?: string;
}

// Use Supabase Edge Function URL
const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const API_BASE_URL = `${SUPABASE_URL}/functions/v1/business-map-settings`;

export class BusinessMapService {
  
  /**
   * Get authentication headers for Supabase
   */
  private static async getAuthHeaders(): Promise<HeadersInit> {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };

    // Try multiple ways to get the auth token
    let accessToken = null;
    
    // Method 1: Try the specific localStorage key
    const supabaseAuthKey = localStorage.getItem('sb-idvgoctfaarxiryxprpy-auth-token');
    console.log('🔍 [BusinessMapService] Auth key found:', !!supabaseAuthKey);
    
    if (supabaseAuthKey) {
      try {
        const authData = JSON.parse(supabaseAuthKey);
        accessToken = authData?.access_token;
        console.log('🔍 [BusinessMapService] Access token found:', !!accessToken);
      } catch (error) {
        console.error('🔍 [BusinessMapService] Error parsing auth token:', error);
      }
    }
    
    // Method 2: Try to get from Supabase client if available
    if (!accessToken && supabase) {
      try {
        const { data: session } = await supabase.auth.getSession();
        if (session?.session?.access_token) {
          accessToken = session.session.access_token;
          console.log('🔍 [BusinessMapService] Got token from Supabase client');
        }
      } catch (error) {
        console.error('🔍 [BusinessMapService] Error getting session from Supabase client:', error);
      }
    }
    
    // Method 3: Try to get from any localStorage key that contains 'auth-token'
    if (!accessToken) {
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.includes('auth-token')) {
          try {
            const authData = JSON.parse(localStorage.getItem(key) || '{}');
            if (authData?.access_token) {
              accessToken = authData.access_token;
              console.log('🔍 [BusinessMapService] Found token in:', key);
              break;
            }
          } catch (error) {
            console.error('🔍 [BusinessMapService] Error parsing token from:', key, error);
          }
        }
      }
    }
    
    if (accessToken) {
      headers['Authorization'] = `Bearer ${accessToken}`;
      console.log('🔍 [BusinessMapService] Authorization header set');
    } else {
      console.log('🔍 [BusinessMapService] No access token found in any location');
    }

    return headers;
  }

  /**
   * Get business map settings
   */
  static async getMapSettings(businessId: string): Promise<BusinessMapSettings | null> {
    try {
      console.log('🔍 [BusinessMapService] Fetching map settings for business:', businessId);
      console.log('🔍 [BusinessMapService] API URL:', `${API_BASE_URL}/${businessId}`);
      
      const headers = await this.getAuthHeaders();
      console.log('🔍 [BusinessMapService] Auth headers:', Object.keys(headers));
      
      const response = await fetch(`${API_BASE_URL}/${businessId}`, {
        headers: headers,
      });
      
      console.log('🔍 [BusinessMapService] Response status:', response.status);
      console.log('🔍 [BusinessMapService] Response ok:', response.ok);
      
      if (!response.ok) {
        if (response.status === 404) {
          console.log('🔍 [BusinessMapService] No settings found (404)');
          return null; // No settings found
        }
        const errorText = await response.text();
        console.error('🔍 [BusinessMapService] HTTP error:', response.status, errorText);
        throw new Error(`HTTP error! status: ${response.status}, body: ${errorText}`);
      }
      
      const data = await response.json();
      console.log('🔍 [BusinessMapService] Response data:', data);
      
      const result = data.success ? data.data : null;
      console.log('🔍 [BusinessMapService] Final result:', result);
      
      return result;
    } catch (error) {
      console.error('🔍 [BusinessMapService] Error fetching business map settings:', error);
      return null;
    }
  }

  /**
   * Set/Update business home pin location
   */
  static async setHomePin(
    businessId: string, 
    homePin: BusinessHomePin, 
    userId: string
  ): Promise<boolean> {
    try {
      const response = await fetch(`${API_BASE_URL}/home-pin`, {
        method: 'POST',
        headers: await this.getAuthHeaders(),
        body: JSON.stringify({
          business_id: businessId,
          home_lat: homePin.lat,
          home_lng: homePin.lng,
          home_address: homePin.address,
          default_zoom: 15,
          map_type: 'satellite',
          updated_by: userId
        })
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP error! status: ${response.status}, body: ${errorText}`);
      }
      
      const result = await response.json();
      return result.success || false;
    } catch (error) {
      console.error('Error setting business home pin:', error);
      return false;
    }
  }

  /**
   * Remove business home pin
   */
  static async removeHomePin(businessId: string, userId: string): Promise<boolean> {
    try {
      const response = await fetch(`${API_BASE_URL}/home-pin`, {
        method: 'DELETE',
        headers: await this.getAuthHeaders(),
        body: JSON.stringify({
          business_id: businessId,
          updated_by: userId
        })
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP error! status: ${response.status}, body: ${errorText}`);
      }
      
      const result = await response.json();
      return result.success || false;
    } catch (error) {
      console.error('Error removing business home pin:', error);
      return false;
    }
  }

  /**
   * Get business home pin coordinates only
   */
  static async getHomePin(businessId: string): Promise<BusinessHomePin | null> {
    const settings = await this.getMapSettings(businessId);
    
    if (settings?.home_lat && settings?.home_lng) {
      return {
        lat: settings.home_lat,
        lng: settings.home_lng,
        address: settings.home_address
      };
    }
    return null;
  }

  /**
   * Check if business has home pin set
   */
  static async hasHomePin(businessId: string): Promise<boolean> {
    const homePin = await this.getHomePin(businessId);
    return homePin !== null;
  }
} 